import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { getAuth, updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { api } from '../utils/apiService';
import { API_CONFIG } from '../utils/config';
import { useAuth } from '../context/AuthContext';

const PersonalProfileForm = ({ open, onClose }) => {
  const auth = getAuth();
  const { updateUser, currentUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarPath, setAvatarPath] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('PersonalProfile - Token exists:', !!token);
        console.log('PersonalProfile - API Base URL:', API_CONFIG.API_URL);

        if (!token) {
          console.warn('No auth token found');
          return;
        }

        console.log('PersonalProfile - Fetching user profile from:', `${API_CONFIG.API_URL}/users/profile`);
        const response = await api.get('/users/profile');
        console.log('PersonalProfile - Profile response:', response.data);

        const userData = response.data.user;
        setFormData(prev => ({
          ...prev,
          displayName: userData.username || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        const serverPhotoUrl = userData.photoURL || '';
        setAvatarPath(serverPhotoUrl);

        let displayUrl = '';
        if (serverPhotoUrl) {
          if (serverPhotoUrl.startsWith('http')) {
            displayUrl = serverPhotoUrl;
          } else if (!serverPhotoUrl.startsWith('blob:')) {
            displayUrl = `${API_CONFIG.BASE_URL}/${serverPhotoUrl.replace(/^\/+/, '')}`;
          }
        }
        setAvatarUrl(displayUrl);
      } catch (error) {
        console.error('PersonalProfile - Error loading user data:', error);
        console.error('PersonalProfile - Error response:', error.response?.data);
        console.error('PersonalProfile - Request URL:', error.config?.url);
        console.error('PersonalProfile - Full URL:', error.config?.baseURL + error.config?.url);

        // Fallback to Firebase data if MongoDB data fetch fails
        if (auth.currentUser) {
          console.log('PersonalProfile - Using Firebase fallback data');
          setFormData(prev => ({
            ...prev,
            displayName: auth.currentUser.displayName || '',
            email: auth.currentUser.email || '',
            phoneNumber: '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }));
          const firebasePhotoUrl = auth.currentUser.photoURL || '';
          if (firebasePhotoUrl && !firebasePhotoUrl.startsWith('blob:')) {
            setAvatarUrl(firebasePhotoUrl);
            setAvatarPath(firebasePhotoUrl);
          } else {
            setAvatarUrl('');
            setAvatarPath('');
          }
        }
      }
    };

    if (open) {
      loadUserData();
    }
  }, [open]);

  const validateCurrentPassword = async (password) => {
    console.log('Validating current password...');
    if (!password) {
      console.log('Password is empty, clearing error');
      setPasswordError('');
      return;
    }

    // Check if user is authenticated
    if (!auth.currentUser) {
      console.log('No authenticated user, skipping password validation');
      setPasswordError('');
      return;
    }

    try {
      // For Google-authenticated users, we'll skip the password validation
      // since they don't have a password set
      if (auth.currentUser.providerData && auth.currentUser.providerData[0]?.providerId === 'google.com') {
        console.log('User is authenticated with Google, skipping password validation');
        setPasswordError('');
        return;
      }

      console.log('Creating credential with email:', auth.currentUser.email);
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        password
      );
      console.log('Attempting to reauthenticate...');
      await reauthenticateWithCredential(auth.currentUser, credential);
      console.log('Reauthentication successful');
      setPasswordError('');
    } catch (error) {
      console.error('Password validation error:', error);
      console.log('Error code:', error.code);
      console.log('Error message:', error.message);

      // If the user is authenticated with Google, don't show the error
      if (auth.currentUser?.providerData && auth.currentUser.providerData[0]?.providerId === 'google.com') {
        setPasswordError('');
      } else {
        setPasswordError('Current password is not correct');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Input changed:', name, value ? 'value provided' : 'empty value');
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear password error when user starts typing again
    if (name === 'currentPassword') {
      console.log('Clearing password error on new input');
      setPasswordError('');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      setMessage({ type: 'error', text: 'Only image files are allowed' });
      return;
    }

    setUploadingAvatar(true);
    setMessage({ type: '', text: '' });

    try {
      const uploadData = new FormData();
      uploadData.append('files', file);

      const response = await api.post('/upload', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Extract the raw path from upload API response: { "data": ["uploads/me-xxx.png"] }
      let uploadedPath = '';
      if (response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        uploadedPath = response.data.data[0];
      } else {
        uploadedPath = response.data?.url || response.data?.imageUrl || response.data?.path || response.data;
      }

      if (uploadedPath && typeof uploadedPath === 'string') {
        // avatarPath stores the raw server path (e.g. "uploads/me-xxx.png") — used as photoURL in profile update
        setAvatarPath(uploadedPath);
        // avatarUrl is the full URL used only for <Avatar> preview display
        const fullUrl = uploadedPath.startsWith('http')
          ? uploadedPath
          : `${API_CONFIG.BASE_URL}/${uploadedPath.replace(/^\/+/, '')}`;
        setAvatarUrl(fullUrl);
        setAvatarFile(null);
        setMessage({ type: 'success', text: 'Avatar uploaded successfully! Remember to save changes.' });
      } else {
        throw new Error('Invalid URL returned from server');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to upload image' });
    } finally {
      setUploadingAvatar(false);
      // Clear input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('====== PERSONAL PROFILE SAVE PROCESS STARTED ======');
    console.log('1. Save button clicked. Event triggered.');
    
    console.log('2. Loading state changed to true');
    setLoading(true);
    setMessage({ type: '', text: '' });

    console.log('3. Current form data before submission:', { ...formData, currentPassword: '[HIDDEN]', newPassword: '[HIDDEN]', confirmPassword: '[HIDDEN]' });
    console.log('   Current avatar file attached:', avatarFile ? avatarFile.name : 'None');
    console.log('   Current avatar path:', avatarPath);
    console.log('   Current avatar URL:', avatarUrl);

    try {
      const token = localStorage.getItem('token');
      console.log('4. Checking authentication token:', token ? 'Token exists' : 'No token found');

      if (!token) {
        throw new Error('You are not authenticated. Please log in again.');
      }

      // Form validation
      console.log('5. Running form validation...');
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          console.error('   Validation Failed: New passwords do not match');
          throw new Error('New passwords do not match');
        } else {
          console.log('   Validation Passed: New passwords match');
        }
      }

      // avatarPath holds the raw upload path (e.g. "uploads/me-xxx.png") — use this as photoURL payload
      const apiPhotoURL = avatarPath || '';
      // Build full URL for Firebase (needs an absolute URL); fall back to existing avatarUrl if no new upload
      const firebasePhotoURL = apiPhotoURL && !apiPhotoURL.startsWith('http')
        ? `${API_CONFIG.BASE_URL}/${apiPhotoURL.replace(/^\/+/, '')}`
        : apiPhotoURL || avatarUrl || '';

      // Update Firebase profile if user is authenticated with Firebase
      if (auth.currentUser) {
        console.log('6a. Updating Firebase profile. User is authenticated with Firebase.');
        const firebasePayload = {
          displayName: formData.displayName,
          photoURL: firebasePhotoURL,
        };
        console.log('    Firebase Profile Payload:', firebasePayload);
        console.log('    Firebase Request Start');
        await updateProfile(auth.currentUser, firebasePayload);
        console.log('    Firebase Request Completed: Profile updated');

        // Update email in Firebase if changed
        if (formData.email !== auth.currentUser.email) {
          console.log('    Updating email in Firebase from', auth.currentUser.email, 'to', formData.email);
          await updateEmail(auth.currentUser, formData.email);
          console.log('    Firebase email update completed');
        }

        // Update password in Firebase if provided and user is not Google-authenticated
        if (formData.newPassword && auth.currentUser.providerData && auth.currentUser.providerData[0]?.providerId !== 'google.com') {
          console.log('    Updating password in Firebase');
          await updatePassword(auth.currentUser, formData.newPassword);
          console.log('    Firebase password update completed');
        }
      } else if (formData.newPassword) {
        // If no Firebase user but password change requested, show warning
        console.warn('    Cannot change password: No Firebase authentication');
        setMessage({
          type: 'warning',
          text: 'Password change is only available for Firebase authenticated users'
        });
      }

      // Update MongoDB profile — send raw upload path as photoURL (not blob or full URL)
      console.log('6b. Preparing MongoDB profile update.');
      const payloadToSend = {
        username: formData.displayName || '',
        email: formData.email || '',
        phoneNumber: formData.phoneNumber || '',
        photoURL: apiPhotoURL || ''
      };
      
      if (formData.currentPassword) payloadToSend.currentPassword = formData.currentPassword;
      if (formData.newPassword) payloadToSend.newPassword = formData.newPassword;

      // Log the payload entries
      console.log('    MongoDB Payload (JSON):', {
        ...payloadToSend,
        currentPassword: payloadToSend.currentPassword ? '[HIDDEN]' : undefined,
        newPassword: payloadToSend.newPassword ? '[HIDDEN]' : undefined
      });

      console.log('7. Sending API Request to update MongoDB profile');
      console.log('   API Endpoint: /users/profile');
      console.log('   Request Method: PUT');
      console.log('   MongoDB Request Start');
      
      const response = await api.put('/users/profile', payloadToSend);
      
      console.log('   MongoDB Request Completed');
      console.log('8. API Response Data:', response.data);

      console.log('9. Success handling flow initiated');
      
      // Update context and localStorage with the new user data
      console.log('   Triggering context update');
      const updatedUser = response.data?.user || response.data;
      if (updatedUser) {
        updateUser(updatedUser);
      } else {
        updateUser({ ...currentUser, ...payloadToSend });
      }

      // Clear password fields after successful update
      console.log('   Triggering state update: Clearing password fields in formData');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      console.log('   Triggering state update: Setting success message');
      setMessage({
        type: 'success',
        text: 'Profile updated successfully!'
      });

      // Close the form after a short delay to show success message
      console.log('   Scheduling form close in 1.5 seconds');
      setTimeout(() => {
        console.log('   Executing scheduled form close (onClose)');
        onClose();
        console.log('====== PERSONAL PROFILE SAVE PROCESS COMPLETED SUCCESSFULLY ======');
      }, 1500);

    } catch (error) {
      console.error('ERROR. Error handling flow initiated');
      console.error('   Error Details:', error);
      if (error.response) {
        console.error('   Error Response Data:', error.response.data);
        console.error('   Error Response Status:', error.response.status);
      }
      console.log('   Triggering state update: Setting error message');
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Error updating profile'
      });
      console.log('====== PERSONAL PROFILE SAVE PROCESS FAILED ======');
    } finally {
      console.log('10. Finally block reached. Loading state changed to false');
      setLoading(false);
    }
  };

  // Common styling for all textboxes
  const controlStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 40,
      '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: 0,
        borderColor: 'rgba(0, 0, 0, 0.12)'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(67, 97, 238, 0.3)'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4361ee'
      }
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      fontSize: '0.875rem',
      height: '24px',
      '&::placeholder': {
        opacity: 0.7,
        color: 'text.secondary'
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      color: 'text.secondary',
      transform: 'translate(12px, 11px)',
      '&.Mui-focused': {
        color: '#4361ee'
      }
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(12px, -8px) scale(0.75)'
    },
    mb: 1.5
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '4px',
          border: '1px solid rgba(49, 19, 19, 0.08)',
          maxWidth: '500px',
          margin: 'auto'
        }
      }}
    >
      <DialogTitle sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          Personal Profile
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            p: 0.5,
            bgcolor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ px: 2, py: 2 }}>
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2, fontSize: '0.875rem' }}>
              {message.text}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
              mt: 1,
            }}
          >
            <Box
              sx={{
                position: "relative",
                cursor: "pointer",
                display: "inline-block",
              }}
            >
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarUpload}
                disabled={uploadingAvatar}
              />
              <label htmlFor="avatar-upload" style={{ cursor: "pointer", display: "block", position: "relative" }}>
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 80,
                    height: 80,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    opacity: uploadingAvatar ? 0.5 : 1,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>

                {uploadingAvatar && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: "#4361ee",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  <PhotoCameraIcon style={{ fontSize: 14 }} />
                </Box>
              </label>
            </Box>

            <Typography
              variant="caption"
              sx={{
                mt: 1,
                color: "text.secondary",
              }}
            >
              Change Photo
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="Enter your display name"
                sx={controlStyle}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                sx={controlStyle}
                InputProps={{
                  readOnly: false,
                }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                sx={controlStyle}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{
                mb: 1,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: 'text.secondary'
              }}>
                Change Password
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleInputChange}
                onBlur={(e) => validateCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                sx={controlStyle}
                error={!!passwordError}
                helperText={passwordError}
              />
              <TextField
                fullWidth
                label="New Password"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter your new password"
                sx={controlStyle}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your new password"
                sx={controlStyle}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 0,
              px: 3,
              minWidth: '80px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={loading}
            sx={{
              textTransform: 'none',
              borderRadius: 0,
              px: 3,
              minWidth: '80px',
              bgcolor: '#4361ee',
              '&:hover': {
                bgcolor: '#3451dd'
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PersonalProfileForm;